import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { snackbar } from '@occc-app/design-system';
import { TableRowActionsMenu, useConfirmDialog } from '@occc/ui-design-system';
import { useAudienceDeleteMutation } from '@occc-app/dataAccessLayer/audience/mutations';
import { Audience_Status } from '@occc/graphql';

import { routeCreator } from '../../utils';
import { ArchiveAudienceConfirmDialog } from '../../components/ArchiveAudienceConfirmDialog';
import { archiveAudienceError, archiveAudienceSuccess } from '../../copies';

interface AudienceActionsContainerProps {
  id: string;
  name: string;
  status: Audience_Status;
}

export const AudienceActionsContainer: React.FC<AudienceActionsContainerProps> = ({
  id,
  name,
  status,
}) => {
  const navigate = useNavigate();

  const onDeleteCompleted = React.useCallback(() => snackbar.success(archiveAudienceSuccess), []);
  const onDeleteError = React.useCallback(() => snackbar.error(archiveAudienceError), []);

  const [audienceDeleteMutation, { loading: deleteLoading }] = useAudienceDeleteMutation({
    variables: { uuid: id },
    onCompleted: onDeleteCompleted,
    onError: onDeleteError,
    refetchQueries: ['audiences'],
  });

  const { open, setOpen, onCloseButtonClick, onConfirmButtonClick } = useConfirmDialog({
    onConfirm: audienceDeleteMutation,
    shouldStopEventPropagation: true,
  });

  const menuItems = useMemo(() => {
    const items = [];

    if (status === Audience_Status.Draft) {
      items.push({
        label: 'Edit Audience',
        onClick: () => navigate(routeCreator.update(id)),
        dataTestId: 'edit',
      });
    }

    if (status !== Audience_Status.Archived) {
      items.push({
        label: 'Archive Audience',
        onClick: () => setOpen(true),
        dataTestId: 'archive',
      });
    }

    return items;
  }, [status, navigate, id, setOpen]);

  return (
    <>
      <TableRowActionsMenu menuItems={menuItems} />

      {open && (
        <ArchiveAudienceConfirmDialog
          onClose={onCloseButtonClick}
          onConfirm={onConfirmButtonClick}
          loading={deleteLoading}
          audienceName={name}
        />
      )}
    </>
  );
};
